import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Field, ImageField, MetaLine, SaveButton, StatusToggle, VideoField, inputCls } from "../../components/admin/fields";

export default function HomeSettingsPage() {
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/admin/home-settings").then((res) => setForm(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  if (!form) return <p className="eyebrow">Loading…</p>;

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));
    setDirty(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put("/admin/home-settings", form);
      setForm(data);
      setSavedAt(new Date());
      setDirty(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="max-w-2xl space-y-8" data-testid="admin-home-form">
      <div className="flex items-center justify-between">
        <h1 className="h-condensed text-4xl">Home</h1>
        <MetaLine by={form.updated_by} at={savedAt || form.updated_at} />
      </div>

      <Field label="Hero media type">
        <select value={form.hero_media_type || "image"} onChange={set("hero_media_type")} className={inputCls} data-testid="hero-media-type">
          <option value="image">Image</option>
          <option value="video">Video</option>
        </select>
      </Field>

      {form.hero_media_type === "video" ? (
        <>
          <VideoField label="Hero desktop video" value={form.hero_desktop_video} onChange={set("hero_desktop_video")} testid="hero-desktop-video" />
          <VideoField label="Hero mobile video (compressed)" value={form.hero_mobile_video} onChange={set("hero_mobile_video")} testid="hero-mobile-video" />
          <ImageField label="Hero poster image (shown while the video loads)" value={form.hero_poster_image} onChange={set("hero_poster_image")} testid="hero-poster" />
        </>
      ) : (
        <ImageField label="Hero image" value={form.hero_image} onChange={set("hero_image")} alt={form.hero_alt} onAltChange={set("hero_alt")} testid="hero-image" />
      )}

      <Field label="Scroll subtitle" hint="Wrap words in *asterisks* to set them in Jazmin italic.">
        <textarea rows={3} value={form.scroll_subtitle || ""} onChange={set("scroll_subtitle")} className={inputCls} data-testid="scroll-subtitle" />
      </Field>

      <div className="flex items-center gap-6 border-t border-charcoal pt-6">
        <SaveButton busy={busy} dirty={dirty} />
        {error && <p className="text-sm" role="alert" data-testid="admin-home-error">{error}</p>}
        {!dirty && !error && savedAt && <p className="text-xs font-light opacity-60">All changes saved.</p>}
      </div>
    </form>
  );
}
